/**
 * Request/response shapes for `POST /api/help/chat` (`BACKLOG.md` B6a). Shared so `server/src/help/
 * chatRoutes.ts` and the web chat widget cannot validate this differently — the usual reason every
 * other `*Requests.ts` module in this package exists.
 *
 * **`route` is validated against `help/routes.ts`'s closed `HELP_ROUTES` set, not accepted as an
 * arbitrary string.** This is half of the compliance boundary the B6a task brief requires: the other
 * half (`server/src/help/contextGuard.ts`) stops an already-built context object from carrying
 * anything but an allowlisted shape into the outbound LLM call; this schema stops the *inbound*
 * request from ever being able to smuggle something route-shaped-but-arbitrary — a path segment
 * carrying a patient id, say — in as far as `route` in the first place.
 *
 * **`message` is bounded at `MAX_HELP_MESSAGE_LENGTH`.** Not a security control on its own — the
 * real boundary is `contextGuard.ts` — but an unbounded free-text field into an LLM call is an
 * unbounded cost per request regardless, and this project already bounds every other free-text
 * request field the same way (`clinicalRequests.ts`'s note-content bound is the precedent).
 */

import { z } from 'zod';

import { HELP_ROUTES } from './help/routes.ts';

export const MAX_HELP_MESSAGE_LENGTH = 2000;

export const helpChatRequestSchema = z.strictObject({
  message: z.string().min(1).max(MAX_HELP_MESSAGE_LENGTH),
  route: z.enum(HELP_ROUTES),
});

export type HelpChatRequest = z.infer<typeof helpChatRequestSchema>;

export const helpChatResponseSchema = z.strictObject({
  answer: z.string(),
  /** Topic ids the answer was grounded in, if any — surfaced so the widget can offer "read more"
   * links straight to the relevant `/help/:topicId` section, per `invariants.md`'s "deep-linkable
   * sections" requirement for the page those ids point at. */
  matchedTopicIds: z.array(z.string()),
});

export type HelpChatResponse = z.infer<typeof helpChatResponseSchema>;

/** `ai_feature_disabled` (`BACKLOG.md` C20): this org's admin has not opted into the `helpChat` AI
 * feature — see `server/src/ai/orgAiSettings.ts`. Distinct from `help_chat_unavailable` (a transient
 * failure calling the model): this is a deliberate, admin-controlled state, not an outage. */
export const HELP_CHAT_ERROR_CODES = [
  'invalid_request',
  'help_chat_unavailable',
  'ai_feature_disabled',
] as const;
export type HelpChatErrorCode = (typeof HELP_CHAT_ERROR_CODES)[number];

export const helpChatErrorResponseSchema = z.strictObject({
  error: z.enum(HELP_CHAT_ERROR_CODES),
});
